import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { appPath } from '../utils/appRouting';
import {
  FileText,
  Users,
  Shield,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  FolderOpen,
  ChevronRight,
  Star,
  Zap,
  Lock,
  Clock,
  Download,
  MessageSquare,
  Share2,
  Layers,
  BookOpen,
  GraduationCap
} from 'lucide-react';
import { openCookiePreferences } from '../privacy/events';
import logoSvg from '../assets/logo.svg';

const Landing = () => {
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const features = [
    {
      icon: FileText,
      title: 'Documentazione scolastica',
      description: 'PEI, PDP, verbali e documenti: compilazione guidata e archivio ordinato'
    },
    {
      icon: Layers,
      title: 'Classi e alunni',
      description: 'Organizza classi, alunni e materiali in un workspace chiaro e condivisibile'
    },
    {
      icon: Sparkles,
      title: 'AI Didattica',
      description: 'Crea materiali, mappe, verifiche e adattamenti: rapido, controllabile, pronto da usare'
    },
    {
      icon: FolderOpen,
      title: 'Archivio Personale',
      description: 'Documenti e materiali sempre reperibili, per anno e per contesto didattico'
    },
    {
      icon: MessageSquare,
      title: 'Community e collaborazione',
      description: 'Confronto tra docenti, condivisione materiali e supporto tra pari'
    },
    {
      icon: Shield,
      title: 'Sicurezza & GDPR',
      description: 'Pensata per la scuola: privacy by design, controllo accessi e conformità'
    }
  ];

  // Un solo accento di brand per tutte le icone, invece di un colore diverso
  // per ogni card: distinzione data dall'icona stessa, non da un arcobaleno.
  const featureIconClass = 'bg-brand/10 text-brand';

  const benefits = [
    { text: 'Riduci tempi e dispersione: documenti e materiali in un unico posto', icon: Clock },
    { text: 'Supporto AI per preparare e adattare materiali didattici', icon: Sparkles },
    { text: 'Gestione semplice di classi, alunni e attività', icon: Layers },
    { text: 'Condivisione ed export per lavorare in team', icon: Share2 },
    { text: 'Sicurezza e privacy progettate per la scuola', icon: Shield }
  ];

  return (
    <div className="h-[100dvh] overflow-y-auto bg-background text-foreground scroll-smooth">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-background/90 backdrop-blur-md border-b border-border z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={logoSvg}
                alt="Sostegno Pro+"
                className="w-10 h-10 md:w-11 md:h-11"
                draggable={false}
              />
              <div>
                <h1 className="text-lg md:text-xl font-bold text-foreground">SOSTEGNO PRO+</h1>
                <p className="text-xs text-muted-foreground hidden sm:block">Per docenti di sostegno e curricolari</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => scrollToSection('funzionalita')}
                className="hidden md:block text-muted-foreground hover:text-foreground font-medium"
              >
                Funzionalità
              </button>
              <Link
                to="/pricing"
                className="hidden sm:block text-muted-foreground hover:text-foreground font-medium"
              >
                Prezzi
              </Link>
              <Button
                variant="outline"
                onClick={() => navigate(appPath('/login'))}
                className="flex px-3 sm:px-4"
              >
                Accedi
              </Button>
              <Button
                onClick={() => navigate(appPath('/register'))}
                className="flex px-3 sm:px-4 ds-btn-gradient"
              >
                Registrati
                <ChevronRight className="w-4 h-4 ml-1 hidden sm:inline" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-b from-muted/30 via-background to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-brand/10 text-brand px-4 py-2 rounded-full text-sm font-medium mb-6 border border-brand/20">
              <Zap className="w-4 h-4" />
              Per docenti di sostegno e curricolari
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              La piattaforma digitale per il lavoro dei docenti
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Documenti, organizzazione didattica, materiali e collaborazione: tutto in un unico spazio, con supporto AI.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="ds-btn-gradient px-8 text-base"
                onClick={() => navigate(appPath('/register'))}
              >
                Inizia Gratis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 text-base"
                onClick={() => scrollToSection('funzionalita')}
              >
                Scopri le funzionalità
              </Button>
            </div>
            <div className="mt-6 text-sm text-muted-foreground">
              Nessuna carta di credito. Attiva in pochi minuti.
            </div>
          </div>
        </div>
      </section>

      {/* A chi è rivolta */}
      <section id="a-chi" className="py-14 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Pensata per la scuola reale</h3>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Che tu sia docente di sostegno o curricolare, trovi strumenti concreti per lavorare meglio e con più continuità.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-card border border-border rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center mb-4">
                <GraduationCap className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-semibold text-foreground">Docenti di sostegno</h4>
              <p className="text-muted-foreground mt-2">Documentazione, PEI/PDP, materiali e organizzazione: tutto guidato e ordinato.</p>
            </div>

            <div className="p-6 bg-card border border-border rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-semibold text-foreground">Docenti curricolari</h4>
              <p className="text-muted-foreground mt-2">Pianifica, crea verifiche e materiali, gestisci classi e alunni senza perdere tempo.</p>
            </div>

            <div className="p-6 bg-card border border-border rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center mb-4">
                <Users className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-semibold text-foreground">Team docenti / CdC</h4>
              <p className="text-muted-foreground mt-2">Condivisione, collaborazione e community: per lavorare in modo più coordinato.</p>
            </div>
          </div>
        </div>
      </section>


      {/* Features */}
      <section id="funzionalita" className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Cosa puoi fare con SOSTEGNO PRO+
            </h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Una suite di strumenti per documentazione, didattica e collaborazione.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group p-6 bg-card border border-border rounded-2xl hover:shadow-xl hover:border-brand/30 transition-all duration-300"
                >
                  <div className={`w-14 h-14 ${featureIconClass} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h4 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h4>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-brand to-brand-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Perché scegliere SOSTEGNO PRO+
              </h3>
              <p className="text-white/80 text-lg mb-8">
                Progettato per ridurre la complessità e aumentare la qualità del lavoro didattico.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/15">
                      <div className="flex-shrink-0 w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-slate-900" />
                      </div>
                      <p className="text-white text-lg pt-1">{benefit.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                    ))}
                    <span className="text-white font-semibold ml-2">5/5</span>
                  </div>
                  <p className="text-white/80 italic">
                    "Finalmente un posto unico per documenti, materiali e collaborazione. L'AI mi fa risparmiare un sacco di tempo nella preparazione."
                  </p>
                  <p className="text-yellow-400 text-sm font-medium">— Docente, scuola secondaria</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community */}
      <section id="community" className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-brand/10 text-brand px-4 py-2 rounded-full text-sm font-medium mb-4 border border-brand/20">
                <MessageSquare className="w-4 h-4" />
                Community didattica
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Non sei solo: confrontati con altri docenti</h3>
              <p className="text-lg text-muted-foreground mb-6">
                Uno spazio professionale per condividere materiali, fare domande, trovare soluzioni e lavorare meglio in team.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Post e discussioni su casi e metodologie</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Condivisione materiali pronti e riutilizzabili</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Collaborazione e messaggi tra docenti</span>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-3xl shadow-xl border border-border p-8">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">Esempi di utilizzo</div>
                  <span className="text-xs text-muted-foreground">Community</span>
                </div>

                <div className="rounded-2xl border border-border/60 bg-muted/30 p-4">
                  <div className="text-sm font-medium">"Cerco una verifica semplificata su frazioni"</div>
                  <div className="text-sm text-muted-foreground mt-1">Ricevi spunti, materiali e adattamenti da colleghi.</div>
                </div>

                <div className="rounded-2xl border border-border/60 bg-muted/30 p-4">
                  <div className="text-sm font-medium">"Condivido una mappa concettuale pronta"</div>
                  <div className="text-sm text-muted-foreground mt-1">Archivia, esporta e riutilizza in classe quando serve.</div>
                </div>

                <Button className="w-full ds-btn-gradient" onClick={() => navigate(appPath('/register'))}>
                  Entra nella community
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-16 md:py-20 bg-muted/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-3xl shadow-xl border border-border p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-brand/10 text-brand px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Lock className="w-4 h-4" />
                  Sicurezza Garantita
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  Privacy, sicurezza e GDPR
                </h3>
                <p className="text-muted-foreground mb-6">
                  SOSTEGNO PRO+ è progettata con un approccio privacy-by-design: controlli, tracciabilità e protezione dei dati.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span>Conforme GDPR UE 2016/679</span>
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span>Accessi protetti e controlli per ruolo</span>
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span>Minimizzazione dati e impostazioni per la scuola</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="w-48 h-48 bg-brand/10 rounded-full flex items-center justify-center">
                  <Shield className="w-24 h-24 text-brand" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Pronto a lavorare con più ordine e meno stress?
          </h3>
          <p className="text-lg text-muted-foreground mb-8">
            Inizia subito gratuitamente. Nessuna carta di credito richiesta.
          </p>
          <Button
            size="lg"
            className="ds-btn-gradient px-8"
            onClick={() => navigate(appPath('/register'))}
          >
            Inizia Gratis
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Column 1 - Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-brand to-brand-2 rounded-xl flex items-center justify-center">
                  <span className="text-yellow-400 font-bold">S+</span>
                </div>
                <div>
                  <p className="text-white font-semibold">SOSTEGNO PRO+</p>
                  <p className="text-xs">Per docenti di sostegno e curricolari</p>
                </div>
              </div>
              <p className="text-sm text-gray-400">
                Piattaforma digitale per docenti: documenti, didattica, materiali e collaborazione.
              </p>
            </div>

            {/* Column 2 - Legal Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Informazioni Legali</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to={appPath('/terms')} className="hover:text-white transition-colors">
                    Termini e Condizioni
                  </Link>
                </li>
                <li>
                  <Link to={appPath('/note-legali-ai')} className="hover:text-white transition-colors">
                    Note legali – Uso dell’Intelligenza Artificiale
                  </Link>
                </li>
                <li>
                  <Link to={appPath('/privacy')} className="hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to={appPath('/cookie-policy')} className="hover:text-white transition-colors">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={openCookiePreferences}
                    className="hover:text-white transition-colors text-left"
                  >
                    Preferenze cookie
                  </button>
                </li>
              </ul>
            </div>

            {/* Column 3 - Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4">Contatti</h4>
              <ul className="space-y-2 text-sm">
                <li>Email: <a href="mailto:info@sostegnopro.it" className="text-blue-400 hover:underline">info@sostegnopro.it</a></li>
                <li>Supporto: <a href="mailto:support@sostegnopro.it" className="text-blue-400 hover:underline">support@sostegnopro.it</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-center text-sm">
            <p>© 2025 SOSTEGNO PRO+ - Tutti i diritti riservati</p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default Landing;
